import React, { useState } from 'react';
import { Layout } from '@/components/Layout';
import { Wand2, Image as ImageIcon, Loader2, Download, RefreshCw, Crown, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PremiumBadge } from '@/components/PremiumBadge';
import { useUnlock } from '@/contexts/UnlockContext';
import { Link } from 'react-router-dom';
import { supabase } from '@/db/supabase';

export default function AICreator() {
  const { isUnlocked } = useUnlock();
  const [prompt, setPrompt] = useState('');
  const [aspectRatio, setAspectRatio] = useState('1:1');
  const [imageCount, setImageCount] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImages, setGeneratedImages] = useState<string[]>([]);
  const [error, setError] = useState('');

  const aspectRatios = [
    { value: '1:1', label: '正方形 (1:1)', premium: false },
    { value: '16:9', label: '横屏 (16:9)', premium: false },
    { value: '9:16', label: '竖屏 (9:16)', premium: false },
    { value: '4:3', label: '标准横屏 (4:3)', premium: true },
    { value: '3:4', label: '标准竖屏 (3:4)', premium: true },
  ];

  const templates = [
    '一个穿着白色T恤的男人，全身照，正面站立，户外，背景是威尼斯海滩标志，洛杉矶。90年代纪实风格的时尚摄影，胶片颗粒，照片写实。',
    '科技感十足的自媒体封面，蓝色渐变背景，3D立体文字，现代简约风格',
    '温馨的咖啡店内景，暖色调，自然光线，木质桌椅，文艺清新风格',
    '未来主义城市夜景，霓虹灯光，赛博朋克风格，高清细节',
  ];

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      setError('请输入图片描述');
      return;
    }

    // Check premium features
    const selectedRatio = aspectRatios.find(r => r.value === aspectRatio);
    if (selectedRatio?.premium && !isUnlocked) {
      setError('此宽高比为高级功能，请先解锁');
      return;
    }

    if (imageCount > 2 && !isUnlocked) {
      setError('批量生成（3张以上）为高级功能，请先解锁');
      return;
    }

    setIsGenerating(true);
    setError('');
    setGeneratedImages([]);

    try {
      const { data, error: apiError } = await supabase.functions.invoke('ai-image-gen', {
        body: {
          prompt: prompt.trim(),
          aspectRatio,
          n: imageCount,
          promptOptimizer: true,
        }
      });

      if (apiError) {
        throw apiError;
      }

      if (data?.base_resp?.status_code !== 0) {
        throw new Error(data?.base_resp?.status_msg || '图片生成失败');
      }

      const images = data?.data?.image_urls || [];
      if (images.length === 0) {
        throw new Error('未生成任何图片');
      }

      setGeneratedImages(images);
    } catch (err: any) {
      console.error('图片生成错误:', err);
      setError(err.message || '生成失败，请稍后再试');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 md:px-6 py-12 max-w-6xl">
        <div className="flex flex-col items-center text-center mb-12">
          <div className="bg-primary/10 p-4 rounded-2xl mb-4">
            <Wand2 className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-4xl font-extrabold tracking-tight mb-4">AI 创意生成</h1>
          <p className="text-muted-foreground text-lg max-w-xl">
            使用 AI 生成封面、海报、配图，让创作更简单
          </p>
        </div>

        {!isUnlocked && (
          <div className="mb-8 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-3xl p-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Crown className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                <div className="text-left">
                  <h3 className="font-bold">解锁高级功能</h3>
                  <p className="text-sm text-muted-foreground">更多宽高比、批量生成、无限制使用</p>
                </div>
              </div>
              <Button variant="outline" className="border-amber-500/50 text-amber-600 dark:text-amber-400 hover:bg-amber-500/10" asChild>
                <Link to="/unlock">
                  <Lock className="mr-2 h-4 w-4" />
                  立即解锁
                </Link>
              </Button>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left: Input Panel */}
          <div className="space-y-6">
            <div className="bg-card border rounded-3xl p-6 shadow-sm space-y-6">
              <div>
                <label className="text-sm font-bold mb-2 block">图片描述</label>
                <Textarea
                  placeholder="描述你想要生成的图片，越详细越好..."
                  className="min-h-[120px] resize-none"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-bold mb-2 block">宽高比</label>
                  <Select value={aspectRatio} onValueChange={setAspectRatio}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {aspectRatios.map(ratio => (
                        <SelectItem 
                          key={ratio.value} 
                          value={ratio.value}
                          disabled={ratio.premium && !isUnlocked}
                        >
                          <div className="flex items-center gap-2">
                            {ratio.label}
                            {ratio.premium && <PremiumBadge size="sm" />}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-bold mb-2 block">生成数量</label>
                  <Select value={String(imageCount)} onValueChange={(v) => setImageCount(Number(v))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4].map(n => (
                        <SelectItem 
                          key={n} 
                          value={String(n)}
                          disabled={n > 2 && !isUnlocked}
                        >
                          <div className="flex items-center gap-2">
                            {n} 张
                            {n > 2 && <PremiumBadge size="sm" />}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button
                className="w-full h-12 text-lg font-bold"
                onClick={handleGenerate}
                disabled={isGenerating || !prompt.trim()}
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    生成中...
                  </>
                ) : (
                  <>
                    <Wand2 className="mr-2 h-5 w-5" />
                    开始生成
                  </>
                )}
              </Button>

              {error && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-600 dark:text-red-400 text-sm">
                  {error}
                </div>
              )}
            </div>

            {/* Templates */}
            <div className="bg-card border rounded-3xl p-6 shadow-sm">
              <h3 className="text-sm font-bold mb-4 flex items-center gap-2">
                <ImageIcon className="h-4 w-4 text-primary" />
                快速模板
              </h3>
              <div className="space-y-2">
                {templates.map((template, idx) => (
                  <button
                    key={idx}
                    className="w-full text-left p-3 bg-muted/30 hover:bg-muted/50 rounded-xl text-xs transition-colors"
                    onClick={() => setPrompt(template)}
                  >
                    {template}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Result Panel */}
          <div className="bg-card border rounded-3xl p-6 shadow-sm">
            <h3 className="text-sm font-bold mb-4">生成结果</h3>
            
            {generatedImages.length === 0 && !isGenerating && (
              <div className="h-[400px] flex flex-col items-center justify-center text-center border-2 border-dashed rounded-2xl">
                <ImageIcon className="h-16 w-16 text-muted-foreground/20 mb-4" />
                <p className="text-muted-foreground text-sm">
                  输入描述后点击生成，<br />AI 将为你创作精美图片
                </p>
              </div>
            )}

            {isGenerating && (
              <div className="h-[400px] flex flex-col items-center justify-center">
                <Loader2 className="h-12 w-12 text-primary animate-spin mb-4" />
                <p className="text-muted-foreground">AI 正在创作中，请稍候...</p>
              </div>
            )}

            {generatedImages.length > 0 && (
              <div className="space-y-4">
                {generatedImages.map((url, idx) => (
                  <div key={idx} className="relative group">
                    <img
                      src={url}
                      alt={`生成图片 ${idx + 1}`}
                      className="w-full rounded-2xl border"
                    />
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                      <Button
                        size="icon"
                        variant="secondary"
                        className="h-8 w-8 rounded-full shadow-lg"
                        asChild
                      >
                        <a href={url} download={`ai-image-${idx + 1}.jpg`}>
                          <Download className="h-4 w-4" />
                        </a>
                      </Button>
                    </div>
                  </div>
                ))}
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setGeneratedImages([])}
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  重新生成
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Tips */}
        <div className="mt-12 bg-primary/5 border border-primary/20 rounded-3xl p-6">
          <h3 className="font-bold mb-4 flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-primary" />
            使用技巧
          </h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex gap-2">
              <span className="text-primary">•</span>
              <span>描述越详细，生成效果越好（包括风格、颜色、构图等）</span>
            </li>
            <li className="flex gap-2">
              <span className="text-primary">•</span>
              <span>可以参考快速模板，修改后生成自己想要的效果</span>
            </li>
            <li className="flex gap-2">
              <span className="text-primary">•</span>
              <span>生成的图片链接有效期 24 小时，请及时下载保存</span>
            </li>
            <li className="flex gap-2">
              <span className="text-primary">•</span>
              <span>适用场景：自媒体封面、海报设计、配图素材等</span>
            </li>
            {!isUnlocked && (
              <li className="flex gap-2">
                <Crown className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <span className="text-amber-600 dark:text-amber-400">
                  解锁高级模式可使用更多宽高比和批量生成功能
                </span>
              </li>
            )}
          </ul>
        </div>
      </div>
    </Layout>
  );
}
